import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IBackgroundJobStarted } from '@models/backgroundJobs/IBackgroundJob';
import { BACKGROUND_START_REQUEST } from '@services/BackgroundJobsService';
import { ITableDemand } from '@models/winnerSelection/ITableDemand';
import { IOfferCritDetailsFormData } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/OfferCritDetails/IOfferCritDetailsFormData';
import { IOfferRateFormData } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/OfferRate/IOfferRateFormData';
import { IOfferTechAnalysisFormData } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/OfferTechAnalysis/IOfferTechAnalysisFormData';
import { ITablePositions, PositionStatus } from '@models/winnerSelection/ITablePositions';
import { IForms } from '@models/Forms/IForms';
import { ITableKO } from '@models/winnerSelection/ITableKO';
import { IOfferApproveToStageTwoFormData } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/OfferApproveToStageTwo/IOfferApproveToStageTwoFormData';
import { IOfferKOCritDetailsFormData } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/OfferKOCritDetails/IOfferKOCritDetailsFormData';
import { IOfferRevaluateFormData } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/OfferRevaluate/IOfferRevaluateFormData';
import { IOfferRatePKOFormData } from '@molecules/formbuilder/controls/CustomComponent/components/WinnerSelection/OfferRatePKO/IOfferRatePKOFormData';

export class WinnerSelectionService {
    /** «Скачать документацию» предложения фоновой задачей `attachmentsArchive`; ответ 202 — `{ jobId }`. */
    static async startOfferAttachmentsInBackground(
        offerId: string,
        tableKey: string,
        actionKey: string,
    ): Promise<IBackgroundJobStarted> {
        const response = await $api.post<IBackgroundJobStarted>(
            `winnerSelection/downloadAttachmentsInBackground/${encodeURIComponent(offerId)}/${encodeURIComponent(tableKey)}/${encodeURIComponent(actionKey)}`,
            undefined,
            BACKGROUND_START_REQUEST,
        );
        return response.data;
    }

    static async fetchTableDemandData(
        docId: string,
        tableKey: string,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<ITableDemand>> {
        return $api.get<ITableDemand>(`/winnerSelection/tableDemand/${docId}/${tableKey}`, { signal });
    }

    static async saveTableDemandOfferCrit(
        offerId: string,
        demandId: string,
        tableKey: string,
        formData: IOfferCritDetailsFormData,
    ): Promise<AxiosResponse> {
        return $api.post(`/winnerSelection/tableDemand/${offerId}/${demandId}/${tableKey}/crit`, formData);
    }

    static async saveTableDemandOfferRate(
        offerId: string,
        demandId: string,
        tableKey: string,
        formData: IOfferRateFormData,
    ): Promise<AxiosResponse> {
        return $api.post(`/winnerSelection/tableDemand/${offerId}/${demandId}/${tableKey}/rate`, formData);
    }

    static async saveTableDemandOfferRatePKO(
        offerId: string,
        demandId: string,
        tableKey: string,
        formData: IOfferRatePKOFormData,
    ): Promise<AxiosResponse> {
        return $api.post(`/winnerSelection/tableDemand/${offerId}/${demandId}/${tableKey}/ratePko`, formData);
    }

    static async saveTableDemandOfferTechAnalysis(
        offerId: string,
        demandId: string,
        tableKey: string,
        formData: IOfferTechAnalysisFormData,
    ): Promise<AxiosResponse> {
        return $api.post(`/winnerSelection/tableDemand/${offerId}/${demandId}/${tableKey}/techAnalysis`, formData);
    }

    static async saveTableDemandOfferApproveToStageTwo(
        offerId: string,
        demandId: string,
        tableKey: string,
        formData: IOfferApproveToStageTwoFormData,
    ): Promise<AxiosResponse> {
        return $api.post(`/winnerSelection/tableDemand/${offerId}/${demandId}/${tableKey}/approveToStageTwo`, formData);
    }

    static async saveTableDemandOfferRevaluate(
        offerId: string,
        demandId: string,
        tableKey: string,
        formData: IOfferRevaluateFormData,
    ): Promise<AxiosResponse> {
        return $api.post(`/winnerSelection/tableDemand/${offerId}/${demandId}/${tableKey}/revaluate`, formData);
    }

    // -------------------------------------------------------------------------------------------------------------------

    /**
     * Список позиций по закупке.
     * @param includeOffersInfo при true бэк дополнительно заполняет
     *   status / winnerName / offersCount / hasMainOffer (требует N+1 чтений offers).
     *   На больших списках (1500+) лучше не использовать — вместо этого фронт
     *   догружает офферс-инфо чанками через fetchPositionsOffersInfo.
     */
    static async fetchTablePositionsData(
        docId: string,
        tableKey: string,
        includeOffersInfo = false,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<ITablePositions>> {
        return $api.get<ITablePositions>(`/winnerSelection/tablePositions/${docId}/${tableKey}`, {
            params: { includeOffersInfo },
            signal,
        });
    }

    /** Batch-подгрузка offers-info по позициям — для ленивого заполнения статусов/победителей. */
    static async fetchPositionsOffersInfo(
        docId: string,
        tableKey: string,
        keys: string[],
        signal?: AbortSignal,
    ): Promise<AxiosResponse<Record<string, {
        offersCount: number;
        hasMainOffer: boolean;
        /** Первый победитель (backward-compat) — используйте winnerNames для полного списка. */
        winnerName: string | null;
        /** Имена ВСЕХ победителей (offers с result === "Основной"). */
        winnerNames: string[];
        status: PositionStatus;
    }>>> {
        return $api.post(
            `/winnerSelection/tablePositions/${docId}/${tableKey}/offersInfo`,
            { keys },
            { signal },
        );
    }

    // -------------------------------------------------------------------------------------------------------------------

    // Возвращает модель для формы
    static async fetchCustomFormScheme(tableKey: string, actionKey: string): Promise<AxiosResponse<IForms>> {
        return $api.get<IForms>(`/winnerSelection/customFormScheme/${tableKey}/${actionKey}`);
    }

    // -------------------------------------------------------------------------------------------------------------------

    static async fetchTableKOData(
        docId: string,
        tableKey: string,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<ITableKO>> {
        return $api.get<ITableKO>(`/winnerSelection/tableKO/${docId}/${tableKey}`, { signal });
    }

    static async saveTableKOOfferCrit(
        offerId: string,
        demandId: string,
        tableKey: string,
        formData: IOfferKOCritDetailsFormData,
    ): Promise<AxiosResponse> {
        return $api.post(`/winnerSelection/tableKO/${offerId}/${demandId}/${tableKey}/crit`, formData);
    }
}
